import React from "react";
import { Helmet } from "react-helmet-async";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

interface PageWrapperProps {
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  children: React.ReactNode;
  actions?: React.ReactNode;
  hideHeader?: boolean;
  titleHelpIcon?: React.ReactNode;
  className?: string;
}

const MainPageWrapper: React.FC<PageWrapperProps> = ({
  title,
  subtitle,
  loading,
  error,
  children,
  actions,
  hideHeader,
  titleHelpIcon,
  className,
}) => {
  const pageTitle = title || "CertainPath";
  const showHeaderBlock = (!!title || !!actions) && !hideHeader;

  return (
    <div className={`relative min-h-full ${className || ""}`}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {showHeaderBlock && (
        <div className="border-b border-gray-200/60 dark:border-slate-700/60 bg-gray-50 dark:bg-slate-950 sticky top-0 z-[5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-12">
              <div className="flex items-center gap-2 min-w-0">
                {title && (
                  <h1 className="text-[15px] font-medium truncate text-gray-900 dark:text-slate-100">
                    {title}
                  </h1>
                )}
                {titleHelpIcon && (
                  <span className="text-gray-500 dark:text-slate-400">
                    {titleHelpIcon}
                  </span>
                )}
                {subtitle && (
                  <span className="hidden sm:inline text-sm text-gray-500 dark:text-slate-400">
                    — {subtitle}
                  </span>
                )}
              </div>
              {actions && (
                <div className="flex-shrink-0 ml-4 [&_button]:h-7 [&_button]:px-2.5 [&_button]:text-xs">
                  {actions}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {children}
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/75 dark:bg-slate-950/90">
          <LoadingIndicator />
        </div>
      )}
    </div>
  );
};

export default MainPageWrapper;
